// Keep these in sync with Python types:
// fountain_agents/lib/ifttt/models.py (PatientWriteStreamScope, RESTRICTED_EVALUATION_SCOPES, RuleCategory)
// The async_task:completed/async_task:failed channel scopes are the producer
// agent names from the AsyncTaskAgentScope Literal in that same models.py.
// Scopes and actions are API-driven via GET /v1/agents-v2/ifttt/rule-options

/** Allowed rule.category values (optional on the rule). */
export const RULE_CATEGORY_VALUES = [
  'self_harm',
  'medication_refill',
  'critical_finding',
] as const;

export type RuleCategoryValue = (typeof RULE_CATEGORY_VALUES)[number];

export const CHANNEL_FRIENDLY_LABELS: Record<string, string> = {
  'fountainmembersagent:member-message': 'Member Zori Chat Message',
  'fountaincliniciansagent:clinician-message': 'Clinician Zori Chat Message',
  'fountainclinicanpopulationagent:population-message':
    'Clinician Population Message',
  'patient_change_stream:create': 'Member Record Created',
  'patient_change_stream:update': 'Member Record Updated',
  'zoom_integration:appointment-event': 'Zoom Appointment Event',
  'async_task:completed': 'Async Task Completed',
  'async_task:failed': 'Async Task Failed',
};

export const ACTION_FRIENDLY_LABELS: Record<string, string> = {
  'tool#update_preferences': 'Update preferences',
  'tool#send_elation_message': 'Send message in Elation',
  'tool#slack_post_message': 'Post message to Slack',
  'tool#slack_reply_to_thread': 'Reply to Slack thread',
  'tool#create_zori_rule': 'Create Zori rule',
  'tool#update_zori_rule': 'Update Zori rule',
  'tool#delete_zori_rule': 'Delete Zori rule',
  'tool#list_zori_rules': 'List Zori rules',
  'tool#slack_add_reaction': 'Add reaction in Slack',
  'tool#update_template': 'Update template',
  'tool#execute_template': 'Execute template',
  'tool#manage_rule': 'Manage rule',
  'tool#message_care_team': 'Message care team',
  'tool#fatal_log': 'Log fatal error',
  'tool#create_async_task': 'Create async task',
  'tool#send_member_notification': 'Send member notification',
  'tool#send_zori_message':
    'Send Zori message to the member (email and/or SMS)',
  'tool#enqueue_document_extraction_v2': 'Run document extraction',
  'tool#assign_survey_to_member': 'Assign survey to member',
};

export const CATEGORY_FRIENDLY_LABELS: Record<string, string> = {
  self_harm: 'Self harm',
  medication_refill: 'Medication refill',
  critical_finding: 'Critical finding',
};

export const SCOPE_FRIENDLY_LABELS: Record<string, string> = {
  'observation:survey_response': 'Observation created from Survey Response',
  medication_request: 'Medication',
  appointment: 'Appointment',
  list: 'List',
  communication: 'Communication (note)',
  clinical_impression:
    'Clinical Impression (Zori Report or Annual Report Section)',
  care_plan: 'Care Plan (Annual Report)',
  questionnaire_response: 'Questionnaire response',
  document_reference: 'Document reference',
  'observation:member-app': 'Member app event',
  'observation:labs': 'Lab result',
  'appointment:deferred': 'Appointment (deferred LLM task)',
  'observation:labs:deferred': 'Lab result (deferred LLM task)',
  'observation:member-app:deferred': 'Member app event (deferred LLM task)',
  send_message: 'Send message',
  appointment_linked: 'Appointment linked',
  appointment_cannot_link_to_member: 'Appointment cannot link to member',
  // On the async_task:completed/async_task:failed channels the scope is the
  // producer agent whose task finished (AsyncTaskAgentScope in models.py).
  'zori-pulse-scan': 'Zori Pulse Scan',
  'document-extraction-v2': 'Document Extraction',
  'template-agent': 'Template Agent',
  'health-summary-agent': 'Health Summary Agent',
};

export function getFriendlyChannel(channel: string | null | undefined): string {
  if (!channel) return '';
  return CHANNEL_FRIENDLY_LABELS[channel] ?? channel;
}

export function getFriendlyAction(action: string | null | undefined): string {
  if (!action) return '';
  return ACTION_FRIENDLY_LABELS[action] ?? action;
}

export function getFriendlyScope(scope: string | null | undefined): string {
  if (!scope) return '';
  return SCOPE_FRIENDLY_LABELS[scope] ?? scope;
}

export function getFriendlyRuleCategory(
  category: string | null | undefined,
): string {
  if (!category) return '';
  return CATEGORY_FRIENDLY_LABELS[category] ?? category;
}

/**
 * Friendly description of a rule's trigger (the "WHEN" half), combining the
 * channel and optional scope, e.g. "Member Record Updated (Document reference)".
 */
export function getRuleTrigger(
  channel: string | null | undefined,
  scope?: string | null | undefined,
): string {
  const friendlyChannel = getFriendlyChannel(channel);
  const friendlyScope = getFriendlyScope(scope);
  if (friendlyChannel && friendlyScope) {
    return `${friendlyChannel} (${friendlyScope})`;
  }
  return friendlyChannel || friendlyScope;
}

/**
 * One-line plain-English summary of a rule, formatted as "trigger → action"
 * using the friendly label maps. Falls back gracefully when either half is
 * missing so the list always has something readable to show.
 */
export function getRuleSummary(rule: {
  channel: string | null | undefined;
  scope?: string | null | undefined;
  action: string | null | undefined;
}): string {
  const trigger = getRuleTrigger(rule.channel, rule.scope);
  const action = getFriendlyAction(rule.action);
  if (trigger && action) {
    return `${trigger} → ${action}`;
  }
  return trigger || action;
}
